import { motion } from 'framer-motion';
import { useTranslation } from 'next-i18next';
import { useState } from 'react';

import Header from '@/common/header';
import { FaqAccordion } from '@/components/trial/shared/faq-accordion';
import { PingCounter } from '@/components/trial/shared/ping-counter';
import { bai_jamjuree } from '@/public/assets/fonts/Bai-Jamjuree';
import { jetbrainsMono } from '@/public/assets/fonts/JetBrains-Mono';

import { CreateAccountModal } from '../../create-account-modal';
import { Footer } from '../../footer';
import type { DefaultTrialProps } from '../../trial-layout-switcher';
import { AccentRain, FloatingCodeParticles, GlowPulse, HeroMatrixRain, ScanlineOverlay } from './components/backgrounds';
import { GLOBAL_KEYFRAMES, MATRIX_BLUE, MATRIX_CODE_BG, MATRIX_DARK, MATRIX_GRAY, MATRIX_GREEN, MATRIX_GREEN_DIM, MATRIX_TERMINAL_BG } from './components/constants';
import { GameCarousel, GlitchText, GreenPillCard, MulticonnectionDiagram, NoPingAppMockup, RedPillCard, SystemModule, TypewriterText } from './components/interactive';
import { InterceptedTransmission, MatrixSection, SectionTitle, TerminalLine } from './components/ui-helpers';

interface LivePingProps extends DefaultTrialProps {}

export function LivePing({ daysTrial, trialConfigId, trialPageId, isDisabledButton = false }: LivePingProps) {
  const { t } = useTranslation(['components/trial/lp/v2c']);
  const [modalOpen, setModalOpen] = useState(false);

  const faqItems = Array.from({ length: 6 }, (_, i) => ({
    q: t(`faq.items.${i}.q`),
    a: t(`faq.items.${i}.a`),
  }));

  const endorsements = Array.from({ length: 4 }, (_, i) => ({
    name: t(`endorsements.${i}.name`),
    title: t(`endorsements.${i}.title`),
    game: t(`endorsements.${i}.game`),
    quote: t(`endorsements.${i}.quote`),
  }));

  const NOPING_TOOLS = Array.from({ length: 6 }, (_, i) => ({
    nameKey: `tools.${i}.name`,
    descKey: `tools.${i}.description`,
  }));

  return (
    <div className={`${bai_jamjuree.className} ${jetbrainsMono.variable} relative min-h-screen`} style={{ backgroundColor: MATRIX_DARK }}>
      <style>{GLOBAL_KEYFRAMES}</style>

      <Header variant='primary' />

      <div className='relative' style={{ zIndex: 2 }}>
        {/* SECTION 1: HERO */}
        <section className='relative flex min-h-screen flex-col items-center justify-center overflow-hidden px-6 pb-16 pt-20'>
          <div className='pointer-events-none absolute inset-0 overflow-hidden'>
            <HeroMatrixRain />
          </div>

          <div className='pointer-events-none absolute inset-0' style={{ background: `radial-gradient(ellipse 80% 60% at 50% 45%, ${MATRIX_DARK}F0 0%, ${MATRIX_DARK}B0 40%, ${MATRIX_DARK}60 70%, transparent 100%), linear-gradient(to bottom, ${MATRIX_DARK}E0 0%, ${MATRIX_DARK}A0 30%, ${MATRIX_DARK}80 60%, ${MATRIX_DARK}C0 100%)` }} />
          <div className='pointer-events-none absolute inset-0' style={{ background: `radial-gradient(ellipse 50% 35% at 50% 35%, ${MATRIX_GREEN}0A 0%, transparent 70%)` }} />

          <div className='relative z-10 mx-auto w-full max-w-5xl text-center'>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3, delay: 0.05 }} className='mb-8'>
              <span className='font-mono text-xs uppercase tracking-[0.3em] md:text-sm' style={{ fontFamily: 'var(--font-jetbrains), monospace', color: MATRIX_GREEN_DIM }}>
                <TypewriterText text={t('hero.boot_line')} delay={50} speed={15} />
              </span>
            </motion.div>

            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.4 }} className='mb-8 text-5xl font-bold leading-[1.05] md:text-6xl lg:text-7xl xl:text-8xl' style={{ fontFamily: 'var(--font-bai)', color: MATRIX_GREEN, textShadow: `0 0 60px ${MATRIX_GREEN}40, 0 0 120px ${MATRIX_GREEN}15` }}>
              <GlitchText text={t('hero.headline_1')} />
              <br />
              <GlitchText text={t('hero.headline_2')} />
            </motion.h1>

            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5, delay: 0.85 }} className='mx-auto mb-12 max-w-2xl text-lg md:text-xl lg:text-2xl' style={{ fontFamily: 'var(--font-bai)', color: '#E5E7EB', fontWeight: 300 }}>
              {t('hero.subtext_prefix')} <span style={{ color: MATRIX_BLUE }}>{t('hero.subtext_accent')}</span> {t('hero.subtext_middle')} <span style={{ color: MATRIX_GREEN, fontWeight: 600 }}>{t('hero.subtext_highlight')}</span>.
            </motion.p>

            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4, delay: 1.0 }} className='mb-12 flex justify-center'>
              <div className='rounded-2xl p-1' style={{ animation: 'glow-pulse-ring 3s ease-in-out infinite' }}>
                <div className='rounded-xl px-2 py-1' style={{ backgroundColor: `${MATRIX_DARK}E0` }}>
                  <PingCounter startPing={187} endPing={5} duration={3} accentColor={MATRIX_GREEN} size='xl' labelOptimizing={t('ping.optimizing')} labelDone={t('ping.done')} />
                </div>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 1.15 }} className='mb-16'>
              <div style={{ animation: 'cta-glow 2.5s ease-in-out infinite', display: 'inline-block', borderRadius: '0.75rem' }}>
                <motion.button onClick={() => setModalOpen(true)} disabled={isDisabledButton} className='inline-block cursor-pointer rounded-xl border-none px-10 py-4 text-lg font-bold' style={{ background: MATRIX_GREEN, color: MATRIX_DARK, fontFamily: 'var(--font-bai)' }} whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                  {t('hero.cta')}
                </motion.button>
              </div>
              <p className='mt-3 text-sm' style={{ color: MATRIX_GREEN_DIM }}>
                {t('hero.cta_sub')}
              </p>
            </motion.div>

            <FloatingCodeParticles />
          </div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5, delay: 1.8 }} className='absolute bottom-8 left-1/2 -translate-x-1/2'>
            <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' as const }} className='flex flex-col items-center gap-2'>
              <span className='font-mono text-[10px] uppercase tracking-[0.2em]' style={{ fontFamily: 'var(--font-jetbrains), monospace', color: MATRIX_GREEN_DIM }}>
                SCROLL.DOWN
              </span>
              <span style={{ color: MATRIX_GREEN_DIM }}>&#x25BC;</span>
            </motion.div>
          </motion.div>
        </section>

        {/* SECTION 2: RED PILL / GREEN PILL */}
        <section className='relative overflow-hidden px-6 py-20 md:py-28'>
          <GlowPulse color={MATRIX_GREEN} intensity={0.04} />

          <div className='relative mx-auto max-w-5xl'>
            <SectionTitle title={t('sections.choose.title')} terminalLine={t('sections.choose.terminal')} accentWord={t('sections.choose.accent')} />

            <div className='relative grid gap-6 md:grid-cols-2 md:gap-0'>
              <div className='absolute inset-y-0 left-1/2 z-10 hidden -translate-x-1/2 flex-col items-center justify-center md:flex'>
                <div className='w-px flex-1' style={{ background: `linear-gradient(to bottom, transparent, ${MATRIX_GRAY}40, transparent)` }} />
                <div className='my-3 flex size-12 items-center justify-center rounded-full border-2 font-mono text-sm font-bold' style={{ fontFamily: 'var(--font-jetbrains), monospace', borderColor: MATRIX_GRAY, backgroundColor: MATRIX_DARK, color: '#FFFFFF', boxShadow: `0 0 20px ${MATRIX_GREEN}15, 0 0 20px #FF3B3B15` }}>
                  VS
                </div>
                <div className='w-px flex-1' style={{ background: `linear-gradient(to bottom, transparent, ${MATRIX_GRAY}40, transparent)` }} />
              </div>

              <MatrixSection delay={0.1}>
                <RedPillCard />
              </MatrixSection>

              <div className='flex items-center justify-center gap-4 md:hidden'>
                <div className='h-px flex-1' style={{ backgroundColor: `${MATRIX_GRAY}30` }} />
                <div className='flex size-10 items-center justify-center rounded-full border-2 font-mono text-xs font-bold' style={{ fontFamily: 'var(--font-jetbrains), monospace', borderColor: MATRIX_GRAY, backgroundColor: MATRIX_DARK, color: '#FFFFFF' }}>
                  VS
                </div>
                <div className='h-px flex-1' style={{ backgroundColor: `${MATRIX_GRAY}30` }} />
              </div>

              <MatrixSection delay={0.2}>
                <GreenPillCard />
              </MatrixSection>
            </div>
          </div>
        </section>

        {/* SECTION 3: MULTICONNECTION DIAGRAM */}
        <section className='relative overflow-hidden px-6 py-20 md:py-28'>
          <ScanlineOverlay />
          <div className='relative mx-auto max-w-5xl'>
            <SectionTitle title={t('sections.multiconnection.title')} terminalLine={t('sections.multiconnection.terminal')} accentWord={t('sections.multiconnection.accent')} />
            <MulticonnectionDiagram />
          </div>
        </section>

        {/* SECTION 4: MULTI INTERNET */}
        <section className='relative overflow-hidden px-6 py-20 md:py-28'>
          <ScanlineOverlay />
          <div className='relative mx-auto max-w-4xl'>
            <SectionTitle title={t('sections.multi_internet.title')} terminalLine={t('sections.multi_internet.terminal')} accentWord={t('sections.multi_internet.accent')} />

            <MatrixSection delay={0.1}>
              <div className='overflow-hidden rounded-2xl border' style={{ backgroundColor: MATRIX_CODE_BG, borderColor: `${MATRIX_GREEN}25`, boxShadow: `0 0 40px ${MATRIX_GREEN}06` }}>
                <div className='p-4 md:p-8'>
                  <svg viewBox='0 0 800 300' className='h-auto w-full' fill='none'>
                    <defs>
                      <filter id='lp-mi-glow'>
                        <feGaussianBlur in='SourceGraphic' stdDeviation='6' result='blur' />
                        <feMerge>
                          <feMergeNode in='blur' />
                          <feMergeNode in='SourceGraphic' />
                        </feMerge>
                      </filter>
                    </defs>

                    {/* ISP 1 */}
                    <motion.g initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.1, duration: 0.5 }}>
                      <rect x='20' y='25' width='130' height='55' rx='10' stroke={MATRIX_GREEN} strokeWidth='1.5' fill={`${MATRIX_GREEN}08`} />
                      <g transform='translate(38,38)'>
                        <path d='M2 8.5C5.6 5 10.4 5 14 8.5' stroke={MATRIX_GREEN} strokeWidth='1.2' strokeLinecap='round' fill='none' />
                        <path d='M4 11C6.4 9 9.6 9 12 11' stroke={MATRIX_GREEN} strokeWidth='1.2' strokeLinecap='round' fill='none' />
                        <circle cx='8' cy='14' r='1.5' fill={MATRIX_GREEN} />
                      </g>
                      <text x='100' y='48' textAnchor='middle' fill={MATRIX_GREEN} fontSize='10' fontFamily='monospace' fontWeight='bold'>
                        ISP 1
                      </text>
                      <text x='100' y='64' textAnchor='middle' fill={MATRIX_GREEN} fontSize='7' fontFamily='monospace' opacity='0.5'>
                        Fiber 200Mb
                      </text>
                    </motion.g>

                    {/* ISP 2 */}
                    <motion.g initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.25, duration: 0.5 }}>
                      <rect x='20' y='100' width='130' height='55' rx='10' stroke={MATRIX_GREEN} strokeWidth='1.5' fill={`${MATRIX_GREEN}08`} />
                      <g transform='translate(38,112)'>
                        <rect x='2' y='2' width='12' height='10' rx='2' stroke={MATRIX_GREEN} strokeWidth='1.2' fill='none' />
                        <line x1='5' y1='12' x2='5' y2='16' stroke={MATRIX_GREEN} strokeWidth='1.2' />
                        <line x1='8' y1='12' x2='8' y2='16' stroke={MATRIX_GREEN} strokeWidth='1.2' />
                        <line x1='11' y1='12' x2='11' y2='16' stroke={MATRIX_GREEN} strokeWidth='1.2' />
                      </g>
                      <text x='100' y='123' textAnchor='middle' fill={MATRIX_GREEN} fontSize='10' fontFamily='monospace' fontWeight='bold'>
                        ISP 2
                      </text>
                      <text x='100' y='139' textAnchor='middle' fill={MATRIX_GREEN} fontSize='7' fontFamily='monospace' opacity='0.5'>
                        Cable 100Mb
                      </text>
                    </motion.g>

                    {/* ISP 3 - Failed */}
                    <motion.g initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 0.7, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.4, duration: 0.5 }}>
                      <rect x='20' y='175' width='130' height='55' rx='10' stroke={MATRIX_BLUE} strokeWidth='1.5' fill='transparent' strokeDasharray='6 4' />
                      <g transform='translate(38,187)'>
                        <rect x='1' y='10' width='3' height='6' rx='1' fill={MATRIX_BLUE} opacity='0.7' />
                        <rect x='5.5' y='7' width='3' height='9' rx='1' fill={MATRIX_BLUE} opacity='0.4' />
                        <rect x='10' y='4' width='3' height='12' rx='1' fill={MATRIX_BLUE} opacity='0.2' />
                      </g>
                      <text x='100' y='198' textAnchor='middle' fill={MATRIX_BLUE} fontSize='10' fontFamily='monospace' fontWeight='bold'>
                        ISP 3
                      </text>
                      <text x='100' y='214' textAnchor='middle' fill={MATRIX_BLUE} fontSize='7' fontFamily='monospace' opacity='0.4'>
                        DSL 50Mb
                      </text>
                      <motion.g initial={{ opacity: 0, scale: 0 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: 0.8, duration: 0.3 }}>
                        <circle cx='138' cy='179' r='9' fill={MATRIX_DARK} stroke={MATRIX_BLUE} strokeWidth='1.5' />
                        <line x1='134' y1='175' x2='142' y2='183' stroke={MATRIX_BLUE} strokeWidth='2' strokeLinecap='round' />
                        <line x1='142' y1='175' x2='134' y2='183' stroke={MATRIX_BLUE} strokeWidth='2' strokeLinecap='round' />
                      </motion.g>
                    </motion.g>

                    {/* ISP 4 - Standby */}
                    <motion.g initial={{ opacity: 0 }} whileInView={{ opacity: 0.35 }} viewport={{ once: true }} transition={{ delay: 0.55, duration: 0.5 }}>
                      <rect x='20' y='248' width='130' height='44' rx='10' stroke='#6B7280' strokeWidth='1' fill='transparent' strokeDasharray='4 3' />
                      <text x='85' y='267' textAnchor='middle' fill='#6B7280' fontSize='9' fontFamily='monospace' fontWeight='bold'>
                        ISP 4
                      </text>
                      <text x='85' y='281' textAnchor='middle' fill='#6B7280' fontSize='7' fontFamily='monospace' opacity='0.4'>
                        Standby
                      </text>
                    </motion.g>

                    {/* Connection Lines */}
                    <motion.line x1='150' y1='52' x2='330' y2='130' stroke={MATRIX_GREEN} strokeWidth='2' initial={{ pathLength: 0 }} whileInView={{ pathLength: 1, strokeOpacity: 0.8 }} viewport={{ once: true }} transition={{ delay: 0.3, duration: 0.6 }} />
                    <motion.line x1='150' y1='127' x2='330' y2='148' stroke={MATRIX_GREEN} strokeWidth='2' initial={{ pathLength: 0 }} whileInView={{ pathLength: 1, strokeOpacity: 0.8 }} viewport={{ once: true }} transition={{ delay: 0.45, duration: 0.6 }} />
                    <motion.line x1='150' y1='202' x2='330' y2='165' stroke={MATRIX_BLUE} strokeWidth='1.5' strokeDasharray='6 4' initial={{ strokeOpacity: 0 }} animate={{ strokeOpacity: [0.4, 0.15, 0.4] }} transition={{ duration: 2, repeat: Infinity }} />
                    <motion.line x1='150' y1='270' x2='330' y2='178' stroke='#6B7280' strokeWidth='1' strokeDasharray='3 4' initial={{ strokeOpacity: 0 }} whileInView={{ strokeOpacity: 0.2 }} viewport={{ once: true }} transition={{ delay: 0.7 }} />

                    {/* Latency labels */}
                    <motion.g initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.6 }}>
                      <rect x='210' y='72' width='42' height='18' rx='6' fill={MATRIX_DARK} stroke={MATRIX_GREEN} strokeWidth='0.8' />
                      <text x='231' y='84' textAnchor='middle' fill={MATRIX_GREEN} fontSize='8' fontFamily='monospace' fontWeight='bold'>
                        12ms
                      </text>
                    </motion.g>
                    <motion.g initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.7 }}>
                      <rect x='210' y='126' width='42' height='18' rx='6' fill={MATRIX_DARK} stroke={MATRIX_GREEN} strokeWidth='0.8' />
                      <text x='231' y='138' textAnchor='middle' fill={MATRIX_GREEN} fontSize='8' fontFamily='monospace' fontWeight='bold'>
                        18ms
                      </text>
                    </motion.g>
                    <motion.g initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.9 }}>
                      <rect x='206' y='170' width='52' height='18' rx='6' fill={MATRIX_DARK} stroke={MATRIX_BLUE} strokeWidth='0.8' />
                      <text x='232' y='182' textAnchor='middle' fill={MATRIX_BLUE} fontSize='7' fontFamily='monospace' fontWeight='bold'>
                        OFFLINE
                      </text>
                    </motion.g>

                    {/* NoPing Hub */}
                    <motion.g initial={{ scale: 0.8, opacity: 0 }} whileInView={{ scale: 1, opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.3, duration: 0.5 }}>
                      <motion.rect x='330' y='105' width='180' height='95' rx='14' fill={MATRIX_GREEN} opacity='0' animate={{ opacity: [0.06, 0.14, 0.06] }} transition={{ duration: 2.5, repeat: Infinity }} filter='url(#lp-mi-glow)' />
                      <rect x='330' y='105' width='180' height='95' rx='14' stroke={MATRIX_GREEN} strokeWidth='2.5' fill='#0a1a02' />
                      <rect x='336' y='111' width='168' height='83' rx='10' stroke={MATRIX_GREEN} strokeWidth='0.5' fill='none' opacity='0.15' />
                      <g transform='translate(394, 118)'>
                        <path d='M16 2 L28 8 L28 18 C28 24 22 30 16 32 C10 30 4 24 4 18 L4 8 Z' stroke={MATRIX_GREEN} strokeWidth='1.5' fill={`${MATRIX_GREEN}15`} />
                        <motion.path d='M11 17 L15 21 L22 13' stroke={MATRIX_GREEN} strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' fill='none' animate={{ opacity: [0.5, 1, 0.5] }} transition={{ duration: 2, repeat: Infinity }} />
                      </g>
                      <text x='420' y='167' textAnchor='middle' fill={MATRIX_GREEN} fontSize='14' fontFamily='monospace' fontWeight='bold'>
                        NoPing
                      </text>
                      <text x='420' y='183' textAnchor='middle' fill={MATRIX_GREEN} fontSize='9' fontFamily='monospace' opacity='0.5'>
                        Multi Internet
                      </text>
                    </motion.g>

                    {/* NoPing -> Game Server */}
                    <motion.line x1='510' y1='152' x2='630' y2='152' stroke={MATRIX_GREEN} strokeWidth='2.5' animate={{ strokeOpacity: [0.7, 1, 0.7] }} transition={{ duration: 1.5, repeat: Infinity }} />
                    <motion.g initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.8 }}>
                      <rect x='550' y='135' width='36' height='18' rx='6' fill={MATRIX_DARK} stroke={MATRIX_GREEN} strokeWidth='0.8' />
                      <text x='568' y='147' textAnchor='middle' fill={MATRIX_GREEN} fontSize='8' fontFamily='monospace' fontWeight='bold'>
                        5ms
                      </text>
                    </motion.g>

                    {/* Game Server */}
                    <motion.g initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.5, duration: 0.5 }}>
                      <rect x='630' y='120' width='150' height='66' rx='12' fill='#0a1a02' stroke={MATRIX_GREEN} strokeWidth='1.5' />
                      <g transform='translate(646, 134)'>
                        <rect x='0' y='0' width='20' height='7' rx='2' stroke={MATRIX_GREEN} strokeWidth='1' fill='none' />
                        <rect x='0' y='10' width='20' height='7' rx='2' stroke={MATRIX_GREEN} strokeWidth='1' fill='none' />
                        <rect x='0' y='20' width='20' height='7' rx='2' stroke={MATRIX_GREEN} strokeWidth='1' fill='none' />
                        <circle cx='15' cy='3.5' r='1.5' fill={MATRIX_GREEN} opacity='0.7' />
                        <circle cx='15' cy='13.5' r='1.5' fill={MATRIX_GREEN} opacity='0.7' />
                        <motion.circle cx='15' cy='23.5' r='1.5' fill={MATRIX_GREEN} animate={{ opacity: [0.4, 1, 0.4] }} transition={{ duration: 1.2, repeat: Infinity }} />
                      </g>
                      <text x='720' y='150' textAnchor='middle' fill={MATRIX_GREEN} fontSize='10' fontFamily='monospace' fontWeight='bold'>
                        GAME
                      </text>
                      <text x='720' y='164' textAnchor='middle' fill={MATRIX_GREEN} fontSize='10' fontFamily='monospace' fontWeight='bold'>
                        SERVER
                      </text>
                    </motion.g>

                    {/* Animated Packets */}
                    <motion.circle r='4' fill={MATRIX_GREEN} animate={{ cx: [150, 330, 510, 630], cy: [52, 130, 152, 152] }} transition={{ duration: 2.5, repeat: Infinity, ease: 'linear', repeatDelay: 0.3 }} />
                    <motion.circle r='3.5' fill={MATRIX_GREEN} animate={{ cx: [150, 330, 510, 630], cy: [127, 148, 152, 152] }} transition={{ duration: 2.2, repeat: Infinity, ease: 'linear', repeatDelay: 0.8 }} />
                    <motion.circle r='3' fill={MATRIX_GREEN} animate={{ cx: [510, 570, 630], cy: [152, 152, 152] }} transition={{ duration: 1, repeat: Infinity, ease: 'linear', repeatDelay: 1.5 }} />
                    <motion.circle r='2' fill={MATRIX_GREEN} opacity='0.5' animate={{ cx: [150, 240, 330], cy: [52, 91, 130] }} transition={{ duration: 1.8, repeat: Infinity, ease: 'linear', repeatDelay: 0.6 }} />
                    <motion.circle r='2' fill={MATRIX_GREEN} opacity='0.5' animate={{ cx: [150, 240, 330], cy: [127, 137, 148] }} transition={{ duration: 1.6, repeat: Infinity, ease: 'linear', repeatDelay: 1 }} />
                  </svg>

                  <div className='mt-6 flex flex-wrap items-center justify-center gap-6'>
                    <div className='flex items-center gap-2'>
                      <motion.div className='size-3 rounded-full' style={{ backgroundColor: MATRIX_GREEN }} animate={{ scale: [1, 1.3, 1] }} transition={{ duration: 1.5, repeat: Infinity }} />
                      <span className='font-mono text-xs' style={{ color: MATRIX_GREEN }}>
                        {t('sections.multi_internet.status_protected')}
                      </span>
                    </div>
                    <div className='flex items-center gap-2'>
                      <div className='size-3 rounded-full' style={{ background: MATRIX_BLUE }} />
                      <span className='font-mono text-xs' style={{ color: MATRIX_BLUE }}>
                        {t('sections.multi_internet.status_failed')}
                      </span>
                    </div>
                  </div>
                  <div className='mt-3 text-center'>
                    <span className='font-mono text-[10px]' style={{ color: `${MATRIX_GREEN}60` }}>
                      {t('sections.multi_internet.footer_note')}
                    </span>
                  </div>
                </div>
              </div>
            </MatrixSection>
          </div>
        </section>

        {/* SECTION 5: PRODUCT MOCKUP */}
        <section className='px-6 py-20 md:py-28'>
          <div className='mx-auto max-w-4xl'>
            <SectionTitle title={t('sections.interface.title')} terminalLine={t('sections.interface.terminal')} accentWord={t('sections.interface.accent')} />
            <NoPingAppMockup />
            <MatrixSection delay={0.15}>
              <p className='mt-8 text-center font-mono text-xs' style={{ fontFamily: 'var(--font-jetbrains), monospace', color: MATRIX_GREEN_DIM }}>
                {'>'} {t('sections.interface.footer')}
              </p>
            </MatrixSection>
          </div>
        </section>

        {/* SECTION 6: STATS */}
        <section className='px-6 py-20 md:py-28'>
          <div className='mx-auto max-w-3xl'>
            <SectionTitle title={t('sections.diagnostics.title')} terminalLine={t('sections.diagnostics.terminal')} accentWord={t('sections.diagnostics.accent')} />

            <MatrixSection delay={0.15}>
              <div className='mt-10 overflow-hidden rounded-xl border' style={{ backgroundColor: MATRIX_CODE_BG, borderColor: `${MATRIX_GREEN}25` }}>
                <div className='flex items-center gap-2 border-b px-4 py-3' style={{ borderColor: `${MATRIX_GREEN}15` }}>
                  <div className='size-3 rounded-full bg-[#6B7280]' />
                  <div className='size-3 rounded-full bg-[#FEBC2E]' />
                  <div className='size-3 rounded-full bg-[#28C840]' />
                  <span className='ms-3 font-mono text-xs' style={{ fontFamily: 'var(--font-jetbrains), monospace', color: MATRIX_GREEN_DIM }}>
                    noping_diagnostics.sh
                  </span>
                </div>

                <div className='p-5 md:p-8'>
                  <div className='mb-6 font-mono text-xs' style={{ fontFamily: 'var(--font-jetbrains), monospace', color: MATRIX_GREEN_DIM }}>
                    $ noping --status --verbose
                  </div>

                  <TerminalLine label='PING_REDUCTION' value={t('stats.ping_reduction')} delay={0.05} />
                  <TerminalLine label='PACKET_LOSS_FIX' value={t('stats.packet_loss')} delay={0.1} />
                  <TerminalLine label='FPS_INCREASE' value={t('stats.fps_increase')} delay={0.15} />
                  <TerminalLine label='CPU_RAM_BOOST' value={t('stats.cpu_ram')} delay={0.2} />
                  <TerminalLine label='SERVERS_ONLINE' value={t('stats.servers')} delay={0.25} />
                  <TerminalLine label='COUNTRIES_COVERED' value={t('stats.countries')} delay={0.3} />
                  <TerminalLine label='ACTIVE_USERS' value={t('stats.users')} delay={0.35} />
                  <TerminalLine label='GAMES_SUPPORTED' value={t('stats.games')} delay={0.4} />

                  <div className='mt-6 border-t pt-4 font-mono text-xs' style={{ fontFamily: 'var(--font-jetbrains), monospace', color: MATRIX_GREEN, borderColor: `${MATRIX_GREEN}15` }}>
                    <motion.span animate={{ opacity: [1, 0.4, 1] }} transition={{ duration: 2, repeat: Infinity }}>
                      {'>'} {t('stats.all_systems')}
                    </motion.span>
                  </div>
                </div>
              </div>
            </MatrixSection>
          </div>
        </section>

        {/* SECTION 7: FEATURES */}
        <section className='px-6 py-20 md:py-28'>
          <div className='mx-auto max-w-5xl'>
            <SectionTitle title={t('sections.upgrades.title')} terminalLine={t('sections.upgrades.terminal')} accentWord={t('sections.upgrades.accent')} />

            <div className='grid gap-4 sm:grid-cols-2 md:gap-5 lg:grid-cols-3'>
              {NOPING_TOOLS.map((tool, i) => (
                <SystemModule key={i} nameKey={tool.nameKey} descriptionKey={tool.descKey} delay={0.1 * i} />
              ))}
            </div>

            <MatrixSection delay={0.2}>
              <p className='mt-10 text-center font-mono text-xs' style={{ fontFamily: 'var(--font-jetbrains), monospace', color: MATRIX_GREEN_DIM }}>
                {'>'} {t('sections.upgrades.footer')}
              </p>
            </MatrixSection>
          </div>
        </section>

        {/* SECTION 8: PRO PLAYERS */}
        <section className='px-6 py-20 md:py-28'>
          <div className='mx-auto max-w-4xl'>
            <SectionTitle title={t('sections.endorsements.title')} terminalLine={t('sections.endorsements.terminal')} accentWord={t('sections.endorsements.accent')} />

            <div className='grid gap-5 md:grid-cols-2 md:gap-6'>
              {endorsements.map((endorsement, i) => (
                <InterceptedTransmission key={endorsement.name} name={endorsement.name} title={endorsement.title} game={endorsement.game} quote={endorsement.quote} delay={0.08 * i} />
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 9: GAMES */}
        <section className='px-6 py-24 md:py-36'>
          <div className='mx-auto max-w-6xl'>
            <SectionTitle title={t('sections.games.title')} terminalLine={t('sections.games.terminal')} accentWord={t('sections.games.accent')} />

            <MatrixSection delay={0.15}>
              <GameCarousel />
              <p className='mt-8 text-center font-mono text-xs' style={{ fontFamily: 'var(--font-jetbrains), monospace', color: MATRIX_GREEN_DIM }}>
                {t('sections.games.footer')}
              </p>
            </MatrixSection>
          </div>
        </section>

        {/* SECTION 10: HOW IT WORKS */}
        <section className='relative overflow-hidden px-6 py-20 md:py-28'>
          <ScanlineOverlay />
          <div className='relative mx-auto max-w-3xl'>
            <SectionTitle title={t('sections.boot.title')} terminalLine={t('sections.boot.terminal')} accentWord={t('sections.boot.accent')} />

            <div className='space-y-6'>
              {[0, 1, 2].map((i) => (
                <MatrixSection key={i} delay={0.1 * i}>
                  <div className='rounded-lg border p-5 md:p-6' style={{ backgroundColor: MATRIX_TERMINAL_BG, borderColor: `${MATRIX_GREEN}20` }}>
                    <div className='flex items-start gap-4 md:gap-6'>
                      <div className='flex size-12 shrink-0 items-center justify-center rounded-lg font-mono text-xl font-bold md:size-14 md:text-2xl' style={{ fontFamily: 'var(--font-jetbrains), monospace', backgroundColor: `${MATRIX_GREEN}10`, color: MATRIX_GREEN, border: `1px solid ${MATRIX_GREEN}30` }}>
                        {String(i + 1).padStart(2, '0')}
                      </div>
                      <div className='flex-1'>
                        <div className='mb-2 font-mono text-xs' style={{ fontFamily: 'var(--font-jetbrains), monospace', color: MATRIX_GREEN_DIM }}>
                          {t(`boot_steps.${i}.cmd`)}
                        </div>
                        <h3 className='mb-2 text-lg font-semibold md:text-xl' style={{ fontFamily: 'var(--font-bai)', color: '#FFFFFF' }}>
                          {t(`boot_steps.${i}.title`)}
                        </h3>
                        <p className='text-sm leading-relaxed md:text-base' style={{ color: MATRIX_GRAY }}>
                          {t(`boot_steps.${i}.desc`)}
                        </p>
                      </div>
                    </div>
                  </div>
                </MatrixSection>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 11: FAQ */}
        <section className='px-6 py-20 md:py-28'>
          <div className='mx-auto max-w-3xl'>
            <SectionTitle title={t('sections.faq.title')} terminalLine={t('sections.faq.terminal')} accentWord={t('sections.faq.accent')} />

            <MatrixSection delay={0.1}>
              <FaqAccordion items={faqItems} className='space-y-2' itemClassName={`border-[${MATRIX_GREEN}20] bg-[${MATRIX_CODE_BG}]`} triggerClassName='font-mono text-sm' accentColor={MATRIX_GREEN} />
            </MatrixSection>
          </div>
        </section>

        {/* SECTION 12: FINAL CTA */}
        <section className='relative overflow-hidden px-6 py-24 md:py-32'>
          <div className='pointer-events-none absolute inset-y-0 right-0 w-1/3' style={{ WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 40%, black 100%)', maskImage: 'linear-gradient(to right, transparent 0%, black 40%, black 100%)' }}>
            <AccentRain />
          </div>

          <div className='pointer-events-none absolute inset-0' style={{ background: `radial-gradient(ellipse 80% 50% at 50% 50%, ${MATRIX_GREEN}06 0%, transparent 70%)` }} />

          <div className='relative mx-auto max-w-3xl text-center'>
            <MatrixSection>
              <div className='mb-8 font-mono text-xs tracking-wider md:text-sm' style={{ fontFamily: 'var(--font-jetbrains), monospace', color: MATRIX_GREEN_DIM }}>
                {'>'} {t('final_cta.terminal')}
              </div>
            </MatrixSection>

            <MatrixSection delay={0.1}>
              <h2 className='mb-6 text-3xl font-bold leading-tight md:text-5xl lg:text-6xl' style={{ fontFamily: 'var(--font-bai)', color: MATRIX_GREEN, textShadow: `0 0 30px ${MATRIX_GREEN}25` }}>
                {t('final_cta.headline')}
              </h2>
            </MatrixSection>

            <MatrixSection delay={0.15}>
              <p className='mb-4 text-xl md:text-2xl' style={{ fontFamily: 'var(--font-bai)', color: '#E5E7EB', fontWeight: 300 }}>
                {t('final_cta.subtitle')}
              </p>
            </MatrixSection>

            <MatrixSection delay={0.2}>
              <p className='mb-10 text-base md:text-lg' style={{ fontFamily: 'var(--font-bai)', color: MATRIX_GRAY, fontWeight: 300 }}>
                {t('final_cta.description')}
              </p>
            </MatrixSection>

            <MatrixSection delay={0.25}>
              <motion.button onClick={() => setModalOpen(true)} disabled={isDisabledButton} className='inline-block cursor-pointer rounded-xl border-none px-12 py-4 text-lg font-bold' style={{ background: MATRIX_GREEN, color: MATRIX_DARK, fontFamily: 'var(--font-bai)', boxShadow: `0 0 30px ${MATRIX_GREEN}25` }} whileHover={{ scale: 1.03, boxShadow: `0 0 40px ${MATRIX_GREEN}40` }} whileTap={{ scale: 0.97 }}>
                {t('final_cta.cta')}
              </motion.button>
              <p className='mt-3 text-sm' style={{ color: MATRIX_GREEN_DIM }}>
                {t('hero.cta_sub')}
              </p>
            </MatrixSection>

            <MatrixSection delay={0.35}>
              <div className='mt-16 font-mono text-sm md:text-base' style={{ fontFamily: 'var(--font-jetbrains), monospace' }}>
                <span style={{ color: MATRIX_GREEN_DIM }}>{'>'} </span>
                <span style={{ color: MATRIX_GRAY }}>{t('final_cta.closing_prefix')} </span>
                <span className='font-bold' style={{ color: MATRIX_GREEN, textShadow: `0 0 10px ${MATRIX_GREEN}40` }}>
                  {t('final_cta.closing_highlight')}
                </span>
              </div>
            </MatrixSection>
          </div>
        </section>

        <Footer text={t('footer', { year: new Date().getFullYear() })} />

        <CreateAccountModal open={modalOpen} onClose={() => setModalOpen(false)} daysTrial={daysTrial} customTrialData={{ custom: trialConfigId, trialPageId }} isDisabledButton={isDisabledButton} formId='v2c-modal-form' />
      </div>
    </div>
  );
}
